// Client-side search helpers — all calls go through /api/search (server-side proxy)
// The Typesense API key is NEVER sent to the browser.

export interface TypesenseHit {
  id: string;
  text: string;
  book: string;
  title?: string;
  chunk_number?: number;
}

export interface SearchResultItem {
  id: string;
  text: string;
  book: string;
  title?: string;
  chunk_number?: number;
}

export async function searchIslamicTexts(
  query: string,
  bookFilter?: string
): Promise<SearchResultItem[]> {
  if (!query.trim()) return [];

  try {
    const params = new URLSearchParams({ q: query, per_page: '30' });
    if (bookFilter && bookFilter !== 'all') {
      params.set('book', bookFilter);
    }

    const res = await fetch(`/api/search?${params.toString()}`);
    if (res.status === 429) {
      throw new Error('rate_limit');
    }
    if (!res.ok) return [];

    const data = await res.json();
    return data.hits || [];
  } catch (error) {
    if (error instanceof Error && error.message === 'rate_limit') throw error;
    console.error('Search error:', error);
    return [];
  }
}

/**
 * Fetch a page of chunks from a specific book, ordered by id (natural order).
 */
export async function fetchChunksFromBook(
  book: string,
  page: number = 1,
  perPage: number = 3
): Promise<SearchResultItem[]> {
  try {
    const params = new URLSearchParams({
      action: 'chunks',
      book,
      offset: String((page - 1) * perPage),
      per_page: String(perPage),
    });

    const res = await fetch(`/api/search?${params.toString()}`);
    if (!res.ok) return [];

    const data = await res.json();
    return data.chunks || [];
  } catch (error) {
    console.error('fetchChunksFromBook error:', error);
    return [];
  }
}

/**
 * Fetch 3 consecutive chunks starting from a given offset within the same book.
 */
export async function fetchConsecutiveChunks(
  book: string,
  startId: string,
  offset: number = 0
): Promise<{ chunks: SearchResultItem[]; hasMore: boolean; hasPrev: boolean }> {
  try {
    const params = new URLSearchParams({
      action: 'chunks',
      book,
      offset: String(offset),
      per_page: '3',
    });

    const res = await fetch(`/api/search?${params.toString()}`);
    if (!res.ok) return { chunks: [], hasMore: false, hasPrev: false };

    const data = await res.json();
    return {
      chunks: data.chunks || [],
      hasMore: data.hasMore ?? false,
      hasPrev: data.hasPrev ?? false,
    };
  } catch (error) {
    console.error('fetchConsecutiveChunks error:', error);
    return { chunks: [], hasMore: false, hasPrev: false };
  }
}
